/**
 * Coast Investment Terminal — Login Page
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { startAuthentication } from '@simplewebauthn/browser';

interface Props {
  onLogin: (token: string, name?: string, role?: string) => void;
  /**
   * Called when /token returns a preauth token — meaning the user just
   * accepted an invite (or had verification reset) and must now complete
   * email code + MFA enrollment before a real session is issued. App.tsx
   * swaps in the VerifyFirstLoginPage component.
   */
  onFirstLoginRequired?: (preauthToken: string, emailHint?: string, name?: string) => void;
}

const API = 'https://coast-terminal-production.up.railway.app';

const PASSWORD_RULES = [
  { test: (p: string) => p.length >= 8,              label: '8+ characters' },
  { test: (p: string) => /[A-Z]/.test(p),            label: 'Uppercase letter' },
  { test: (p: string) => /[a-z]/.test(p),            label: 'Lowercase letter' },
  { test: (p: string) => /[0-9]/.test(p),            label: 'Number' },
  // eslint-disable-next-line no-useless-escape
  { test: (p: string) => /[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>/?`~]/.test(p), label: 'Special character' },
];

interface Particle { id: number; x: number; y: number; size: number; duration: number; delay: number; opacity: number; }

function generateParticles(count: number): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const seed = (i * 2654435761) >>> 0;
    particles.push({
      id: i,
      x: ((seed % 1000) / 1000) * 100,
      y: (((seed >> 10) % 1000) / 1000) * 100,
      size: 1.5 + ((seed >> 20) % 100) / 50,
      duration: 10 + ((seed >> 5) % 1000) / 50,
      delay: -((seed >> 15) % 10000) / 500,
      opacity: 0.15 + ((seed >> 25) % 100) / 250,
    });
  }
  return particles;
}

const PARTICLES = generateParticles(50);

// Wave paths — each ends at the exact same Y as it starts so tiling is seamless
// Both paths span exactly 1200 units. Doubled to 2400 for the translateX(-50%) scroll trick.
const WAVE_PATH =
  'M0,35 C100,55 200,15 300,35 C400,55 500,15 600,35 ' +
  'C700,55 800,15 900,35 C1000,55 1100,15 1200,35 ' +
  'C1300,55 1400,15 1500,35 C1600,55 1700,15 1800,35 ' +
  'C1900,55 2000,15 2100,35 C2200,55 2300,15 2400,35 ' +
  'L2400,80 L0,80 Z';

const WAVE_PATH_2 =
  'M0,50 C120,30 240,65 360,50 C480,30 600,65 720,50 ' +
  'C840,30 960,65 1080,50 C1200,30 1320,65 1440,50 ' +
  'C1560,30 1680,65 1800,50 C1920,30 2040,65 2160,50 ' +
  'C2280,30 2400,65 2400,50 L2400,80 L0,80 Z';

export default function Login({ onLogin, onFirstLoginRequired }: Props) {
  // Coast is invite-only — public signup is removed. A state hook (never
  // updated) keeps the type wide ('signin' | 'signup') so the existing JSX
  // branches below still compile without a sweeping rewrite.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mode, _setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName]               = useState('');
  const [email, setEmail]             = useState('');
  const [username, setUsername]       = useState('');
  const [password, setPassword]       = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [magicMode, setMagicMode]     = useState(false);
  const [magicEmail, setMagicEmail]   = useState('');
  const [magicSent, setMagicSent]     = useState(false);
  const [mfaPending, setMfaPending]   = useState(false);
  const [mfaUsername, setMfaUsername] = useState('');
  const [mfaCode, setMfaCode]         = useState('');
  // Passkey state: after /token returns webauthn_required we call
  // startAuthentication; if the user cancels (or their device can't do it)
  // we surface an "email code instead" escape hatch driven by these.
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [passkeyUsername, setPasskeyUsername] = useState('');
  const [mounted, setMounted]         = useState(false);
  const [pwFocused, setPwFocused]     = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // switchMode is a no-op now that signup is removed; kept for compatibility
  // with the JSX below (mode toggle UI is hidden via `{false && ...}`).
  const switchMode = useCallback((_m: 'signin' | 'signup') => {
    setError('');
    setPassword('');
    setMagicMode(false);
    setMagicSent(false);
    setMfaPending(false);
  }, []);

  // ── Password login ────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const form = new URLSearchParams();
      form.append('username', username);
      form.append('password', password);
      const res = await axios.post(`${API}/token`, form);

      // 1) First-login gauntlet — verified_at=NULL users get a preauth token
      //    and must complete email + passkey enrollment before real sign-in.
      if (res.data.preauth_token && res.data.next === 'email') {
        onFirstLoginRequired?.(res.data.preauth_token, res.data.email_hint, res.data.name);
        return;
      }

      // 2) Passkey challenge — verified user, at least one passkey enrolled.
      //    Immediately invoke the browser's native credentials prompt
      //    (Touch ID / Face ID / Hello / PIN / QR-to-phone).
      if (res.data.webauthn_required) {
        setPasskeyUsername(res.data.username || username);
        await runPasskey(res.data.options, res.data.challenge_token);
        return;
      }

      // 3) Legacy TOTP path (any account still on TOTP from before the swap).
      if (res.data.mfa_required) {
        setMfaPending(true);
        setMfaUsername(username);
        return;
      }

      // 4) Full access token — verified user, no second factor required
      //    (grandfathered account with no passkey enrolled yet).
      onLogin(res.data.access_token, res.data.name, res.data.role);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Magic link ────────────────────────────────────────────────────────────
  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicEmail.trim()) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/magic-link/request`, { email: magicEmail });
      setMagicSent(true);
    } catch {
      setError('Unable to send link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Passkey assertion ────────────────────────────────────────────────────
  //
  // Runs the browser WebAuthn prompt with the challenge the server just
  // handed us. On success we have an access token. On cancel / failure we
  // leave the user on a small "waiting" panel that offers the email fallback.
  const runPasskey = async (options: any, challengeToken: string) => {
    setPasskeyPending(true);
    setError('');
    try {
      const assertion = await startAuthentication({ optionsJSON: options });
      const res = await axios.post(`${API}/webauthn/login/finish`, {
        challenge_token: challengeToken,
        credential: assertion,
      });
      onLogin(res.data.access_token, res.data.name, res.data.role);
    } catch (err: any) {
      const msg = err?.response?.data?.detail
        || (err?.name === 'NotAllowedError'
          ? 'Passkey prompt was cancelled or timed out.'
          : err?.message)
        || 'Passkey sign-in failed.';
      setError(msg);
      // Stay on the passkey-pending panel so the user can retry or fall back.
    }
  };

  const retryPasskey = async () => {
    if (loading || !passkeyUsername) return;
    setLoading(true);
    setError('');
    try {
      const begin = await axios.post(`${API}/webauthn/login/begin`, { username: passkeyUsername });
      await runPasskey(begin.data.options, begin.data.challenge_token);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not restart the passkey prompt.');
    } finally {
      setLoading(false);
    }
  };

  // Email-code fallback for when the user can't use their passkey right now.
  // Hands off to the normal first-login gauntlet component — on the email
  // step the server recognizes verified_at is already set and returns a
  // full access token instead of advancing to passkey enrollment.
  const useEmailFallback = async () => {
    if (loading || !passkeyUsername) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/webauthn/login/email-fallback`, {
        username: passkeyUsername,
      });
      if (res.data.preauth_token) {
        onFirstLoginRequired?.(res.data.preauth_token, undefined, res.data.name);
      } else {
        setError('Could not send fallback email.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not send fallback email.');
    } finally {
      setLoading(false);
    }
  };

  // ── MFA confirmation ──────────────────────────────────────────────────────
  const handleMfaConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfaCode.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/mfa/confirm`, { username: mfaUsername, code: mfaCode });
      onLogin(res.data.access_token, res.data.name, res.data.role);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = PASSWORD_RULES.filter(r => r.test(password)).length;
  const strengthWidth = `${(passwordStrength / PASSWORD_RULES.length) * 100}%`;
  const strengthColor =
    passwordStrength <= 2 ? '#C83030' :
    passwordStrength <= 3 ? '#D4AF7A' :
    passwordStrength === 4 ? '#3A8BBF' : '#1A8A52';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'EB Garamond', -apple-system, BlinkMacSystemFont, sans-serif", backgroundColor: '#f5f0e8', position: 'relative', overflow: 'hidden' }}>

      {/* Background */}
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(160deg, #f5f0e8 0%, #efe8d8 50%, #e8d4a8 100%)`, pointerEvents: 'none' }} />

      {/* Particles */}
      {PARTICLES.map(p => (
        <div key={p.id} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, width: `${p.size}px`, height: `${p.size}px`, borderRadius: '50%', backgroundColor: '#b8956a', opacity: p.opacity, pointerEvents: 'none', animation: `float ${p.duration}s ease-in-out ${p.delay}s infinite` }} />
      ))}

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', width: '100%', minHeight: '100vh' }}>

        {/* ── Left panel ── */}
        <div style={{ width: '45%', minWidth: '320px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '60px', borderRight: '1px solid #c4a870', backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', bottom: '-20%', left: '-10%', width: '60%', height: '60%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(58, 139, 191, 0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

          <div style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(-12px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, letterSpacing: '10px', fontFamily: "'Fraunces', Georgia, serif", background: 'linear-gradient(90deg, #D4AF7A 0%, #E8C98A 35%, #FFF5D6 50%, #E8C98A 65%, #D4AF7A 100%)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmer 5s linear infinite', display: 'inline-block' }}>COAST</div>
            <div style={{ width: '40px', height: '1px', background: 'linear-gradient(90deg, rgba(212,175,122,0.7), transparent)', margin: '14px 0 10px', animation: mounted ? 'borderDraw 1.2s ease 0.3s both' : 'none' }} />
            <div style={{ fontSize: '9px', color: '#2d7fa0', letterSpacing: '3.5px', textTransform: 'uppercase', fontWeight: 700 }}>Investment Terminal</div>
          </div>

          <div style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(20px)', transition: 'opacity 0.9s ease 0.15s, transform 0.9s ease 0.15s' }}>
            <div style={{ fontSize: '40px', fontWeight: 300, fontFamily: "'Fraunces', Georgia, serif", color: '#0d3d5c', lineHeight: 1.2, letterSpacing: '0.02em', marginBottom: '6px' }}>Sovereign wealth</div>
            <div style={{ fontSize: '40px', fontWeight: 700, fontFamily: "'Fraunces', Georgia, serif", color: '#0d3d5c', lineHeight: 1.2, letterSpacing: '0.01em', marginBottom: '6px' }}>intelligence,</div>
            <div style={{ fontSize: '40px', fontWeight: 300, fontFamily: "'Fraunces', Georgia, serif", color: '#1a6688', lineHeight: 1.2, letterSpacing: '0.02em', marginBottom: '28px', fontStyle: 'italic' }}>built for institutions.</div>
            <div style={{ fontSize: '13px', color: '#2d7fa0', lineHeight: 1.9, maxWidth: '340px', borderLeft: '2px solid rgba(196,168,112,0.3)', paddingLeft: '16px' }}>
              Coast is an AI analyst platform for institutional real estate funds — underwriting deals, generating investment memos, and analyzing markets in seconds.
            </div>
          </div>

          <div style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(20px)', transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s' }}>
            <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(196,168,112,0.3), transparent)', marginBottom: '28px' }} />
            <div style={{ display: 'flex', gap: '40px' }}>
              {[{ value: '14', label: 'Communities' }, { value: '2,611', label: 'Units' }, { value: '10', label: 'MSAs' }].map(({ value, label }, i) => (
                <div key={label} style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(10px)', transition: `opacity 0.8s ease ${0.35 + i * 0.1}s, transform 0.8s ease ${0.35 + i * 0.1}s` }}>
                  <div style={{ fontSize: '26px', fontWeight: 700, fontFamily: "'Fraunces', Georgia, serif", color: '#b8956a', lineHeight: 1, marginBottom: '5px' }}>{value}</div>
                  <div style={{ fontSize: '9px', color: '#2d7fa0', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 48px', backgroundColor: 'rgba(245,240,232,0.60)' }}>
          <div style={{ width: '100%', maxWidth: '420px', opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(24px)', transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s' }}>

            {/* Mode toggle removed — Coast is invite-only. Signup is disabled. */}
            <div style={{ marginBottom: '24px' }} />
            {/* preserve `switchMode` import usage so linter doesn't complain */}
            {false && <button onClick={() => switchMode('signin')} />}

            {/* Heading */}
            <div style={{ fontSize: '30px', fontWeight: 300, fontFamily: "'Fraunces', Georgia, serif", color: '#0d3d5c', letterSpacing: '0.02em', marginBottom: '6px', lineHeight: 1.2 }}>
              {mode === 'signin' ? 'Welcome back.' : 'Create your account.'}
            </div>
            <div style={{ fontSize: '13px', color: '#2d7fa0', marginBottom: '32px' }}>
              {mode === 'signin' ? 'Sign in to access the Coast terminal.' : 'Get access to the Coast investment platform.'}
            </div>

            {/* ── Passkey in progress screen ──
                Shown while waiting for the browser's native Touch ID /
                Face ID / Hello / QR prompt, and after a failure so the
                user can retry or fall back to email. */}
            {passkeyPending ? (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 300, fontFamily: "'Fraunces', Georgia, serif", color: '#0d3d5c', marginBottom: '8px' }}>
                    Unlock to continue
                  </div>
                  <div style={{ fontSize: '13px', color: '#2d7fa0', lineHeight: 1.6 }}>
                    Use Touch ID, Face ID, Windows Hello, your device PIN, or your phone
                    to prove this is you.
                  </div>
                </div>
                {error && (
                  <div style={{ backgroundColor: 'rgba(200,48,48,0.08)', border: '1px solid rgba(200,48,48,0.22)', borderLeft: '3px solid #C83030', borderRadius: '3px', padding: '11px 14px', marginBottom: '16px', fontSize: '12px', color: '#C83030' }}>
                    {error}
                  </div>
                )}
                <button
                  type="button"
                  onClick={retryPasskey}
                  disabled={loading}
                  style={{ width: '100%', padding: '14px', backgroundColor: loading ? 'rgba(15,32,56,0.8)' : '#D4AF7A', color: loading ? '#3E5070' : '#030A14', border: 'none', borderRadius: '3px', fontWeight: 800, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit', marginBottom: '12px' }}
                >
                  {loading ? 'Waiting for device…' : 'Try Passkey Again'}
                </button>
                <button
                  type="button"
                  onClick={useEmailFallback}
                  disabled={loading}
                  style={{ width: '100%', padding: '12px', backgroundColor: 'transparent', color: '#12526e', border: '1px solid #c4a870', borderRadius: '3px', fontWeight: 700, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit', marginBottom: '16px' }}
                >
                  Sign in with email code instead
                </button>
                <div style={{ textAlign: 'center' }}>
                  <span
                    onClick={() => { setPasskeyPending(false); setPasskeyUsername(''); setError(''); }}
                    style={{ fontSize: '12px', color: '#b8956a', cursor: 'pointer', fontWeight: 600 }}
                  >
                    ← Back to sign in
                  </span>
                </div>
              </div>

            ) : mfaPending ? (
              <form onSubmit={handleMfaConfirm}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 600, fontFamily: "'Fraunces', Georgia, serif", color: '#082f47', marginBottom: '8px' }}>Two-Factor Authentication</div>
                  <div style={{ fontSize: '13px', color: '#2d7fa0', lineHeight: 1.6 }}>Enter the 6-digit code from your authenticator app.</div>
                </div>
                {error && <div style={{ backgroundColor: 'rgba(200,48,48,0.08)', border: '1px solid rgba(200,48,48,0.22)', borderLeft: '3px solid #C83030', borderRadius: '3px', padding: '11px 14px', marginBottom: '16px', fontSize: '12px', color: '#E07070' }}>{error}</div>}
                <StyledInput type="text" placeholder="000000" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} autoFocus style={{ textAlign: 'center', letterSpacing: '10px', fontSize: '24px' }} />
                <button type="submit" disabled={loading || mfaCode.length !== 6} style={{ width: '100%', padding: '14px', backgroundColor: mfaCode.length === 6 && !loading ? '#D4AF7A' : 'rgba(15,32,56,0.8)', color: mfaCode.length === 6 && !loading ? '#030A14' : '#3E5070', border: 'none', borderRadius: '3px', fontWeight: 800, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', cursor: mfaCode.length === 6 && !loading ? 'pointer' : 'default', fontFamily: 'inherit' }}>
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <span onClick={() => { setMfaPending(false); setMfaCode(''); setError(''); }} style={{ fontSize: '12px', color: '#b8956a', cursor: 'pointer', fontWeight: 600 }}>Back to sign in</span>
                </div>
              </form>

            ) : magicMode ? (
              /* ── Magic link screen ── */
              <form onSubmit={handleMagicLink}>
                {!magicSent ? (
                  <>
                    <FieldLabel>Email Address</FieldLabel>
                    <StyledInput type="email" placeholder="you@yourfirm.com" value={magicEmail} onChange={e => setMagicEmail(e.target.value)} required autoComplete="email" />
                    {error && <div style={{ backgroundColor: 'rgba(200,48,48,0.08)', border: '1px solid rgba(200,48,48,0.22)', borderLeft: '3px solid #C83030', borderRadius: '3px', padding: '11px 14px', marginBottom: '16px', fontSize: '12px', color: '#E07070' }}>{error}</div>}
                    <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: loading ? 'rgba(15,32,56,0.8)' : '#D4AF7A', color: loading ? '#3E5070' : '#030A14', border: 'none', borderRadius: '3px', fontWeight: 800, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit', marginBottom: '16px' }}>
                      {loading ? 'Sending...' : 'Send Login Link'}
                    </button>
                  </>
                ) : (
                  <div style={{ backgroundColor: 'rgba(26,122,74,0.08)', border: '1px solid rgba(26,122,74,0.25)', borderRadius: '3px', padding: '20px', textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '16px', fontFamily: "'Fraunces', Georgia, serif", color: '#1A8A52', fontWeight: 600, marginBottom: '8px' }}>Check your email</div>
                    <div style={{ fontSize: '12px', color: '#2d7fa0', lineHeight: 1.6 }}>A login link has been sent to <strong style={{ color: '#12526e' }}>{magicEmail}</strong>. It expires in 15 minutes.</div>
                  </div>
                )}
                <div style={{ textAlign: 'center' }}>
                  <span onClick={() => { setMagicMode(false); setMagicSent(false); setMagicEmail(''); setError(''); }} style={{ fontSize: '12px', color: '#b8956a', cursor: 'pointer', fontWeight: 600 }}>Use password instead</span>
                </div>
              </form>

            ) : (
              /* ── Standard password form ── */
              <form onSubmit={handleSubmit} autoComplete="on">
                {mode === 'signup' && (
                  <>
                    <FieldLabel>Full Name</FieldLabel>
                    <StyledInput type="text" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} required autoComplete="name" maxLength={100} />
                    <FieldLabel>Email Address</FieldLabel>
                    <StyledInput type="email" placeholder="you@yourfirm.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" maxLength={254} />
                  </>
                )}

                <FieldLabel>Username</FieldLabel>
                <StyledInput type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} required autoComplete="username" maxLength={30} />

                <FieldLabel>Password</FieldLabel>
                <div style={{ position: 'relative', marginBottom: mode === 'signup' ? '8px' : '20px' }}>
                  <StyledInput type={showPass ? 'text' : 'password'} placeholder="••••••••••" value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)} required autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} maxLength={128} style={{ paddingRight: '44px', marginBottom: 0 }} />
                  <button type="button" onClick={() => setShowPass(v => !v)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#2d7fa0', fontSize: '12px', fontFamily: 'inherit', letterSpacing: '1px', padding: '2px 4px', transition: 'color 0.15s' }} onMouseEnter={e => (e.currentTarget.style.color = '#D4AF7A')} onMouseLeave={e => (e.currentTarget.style.color = '#3E5070')}>
                    {showPass ? 'HIDE' : 'SHOW'}
                  </button>
                </div>

                {mode === 'signup' && (
                  <div style={{ marginBottom: '20px', overflow: 'hidden', maxHeight: pwFocused || password.length > 0 ? '120px' : '0', transition: 'max-height 0.35s ease' }}>
                    <div style={{ height: '2px', backgroundColor: 'rgba(26, 46, 68, 0.8)', borderRadius: '1px', marginBottom: '10px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: strengthWidth, backgroundColor: strengthColor, borderRadius: '1px', transition: 'width 0.3s ease, background-color 0.3s ease' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 16px' }}>
                      {PASSWORD_RULES.map(rule => {
                        const passed = rule.test(password);
                        return (
                          <div key={rule.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: passed ? '#1A8A52' : '#3E5070', transition: 'color 0.2s' }}>
                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: passed ? '#1A8A52' : '#1A2E44', flexShrink: 0, transition: 'background-color 0.2s' }} />
                            {rule.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {error && <div style={{ backgroundColor: 'rgba(200, 48, 48, 0.08)', border: '1px solid rgba(200, 48, 48, 0.22)', borderLeft: '3px solid #C83030', borderRadius: '3px', padding: '11px 14px', marginBottom: '16px', fontSize: '12px', color: '#E07070', lineHeight: 1.5, animation: 'fadeSlideUp 0.3s ease both' }}>{error}</div>}

                {/* Submit button */}
                <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: loading ? 'rgba(15,32,56,0.8)' : '#D4AF7A', color: loading ? '#3E5070' : '#030A14', border: loading ? '1px solid #1A2E44' : 'none', borderRadius: '3px', fontWeight: 800, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease', position: 'relative', overflow: 'hidden' }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.backgroundColor = '#E8C98A'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,175,122,0.25)'; } }}
                  onMouseLeave={e => { if (!loading) { e.currentTarget.style.backgroundColor = '#D4AF7A'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; } }}>
                  {loading ? (<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><LoadingDots />Processing</span>) : (mode === 'signin' ? 'Sign In' : 'Create Account')}
                </button>

                {/* Magic link toggle — outside and below the button */}
                {mode === 'signin' && (
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <span onClick={() => { setMagicMode(true); setError(''); }} style={{ fontSize: '12px', color: '#b8956a', cursor: 'pointer', fontWeight: 600, transition: 'color 0.15s' }} onMouseEnter={e => (e.currentTarget.style.color = '#E8C98A')} onMouseLeave={e => (e.currentTarget.style.color = '#D4AF7A')}>
                      Sign in with email link instead
                    </span>
                  </div>
                )}
              </form>
            )}

            {/* Invite-only notice replaces the previous Create Account link. */}
            {!mfaPending && !magicMode && !passkeyPending && (
              <div style={{ marginTop: '24px', fontSize: '11px', color: '#3E5070', textAlign: 'center', letterSpacing: '1px' }}>
                Coast is invite-only. Ask an administrator for access.
              </div>
            )}

            <div style={{ marginTop: '40px', paddingTop: '24px', borderTop: '1px solid rgba(26, 46, 68, 0.5)', fontSize: '10px', color: '#1E2E40', textAlign: 'center', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Proprietary · Coast Investment Terminal
            </div>
          </div>
        </div>
      </div>

      {/* Ocean Wave — 3 seamless layers, coast ocean palette, forever loop */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, width: '100%', height: '80px', pointerEvents: 'none', overflow: 'hidden', zIndex: 10 }}>
        <svg viewBox="0 0 2400 80" preserveAspectRatio="none" style={{ position: 'absolute', bottom: 0, width: '200%', height: '100%', animation: 'waveScroll 24s linear infinite', opacity: 0.3 }}>
          <path d={WAVE_PATH} fill="#4a9cb8" />
        </svg>
        <svg viewBox="0 0 2400 80" preserveAspectRatio="none" style={{ position: 'absolute', bottom: 0, width: '200%', height: '100%', animation: 'waveScroll 16s linear infinite reverse', opacity: 0.4 }}>
          <path d={WAVE_PATH_2} fill="#2d7fa0" />
        </svg>
        <svg viewBox="0 0 2400 80" preserveAspectRatio="none" style={{ position: 'absolute', bottom: 0, width: '200%', height: '100%', animation: 'waveScroll 10s linear infinite', opacity: 0.55 }}>
          <path d={WAVE_PATH} fill="#1a6688" />
        </svg>
      </div>

      <style>{`
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes waveScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes borderDraw { from { width: 0; opacity: 0; } to { width: 40px; opacity: 1; } }
        @keyframes dotBounce { 0%, 80%, 100% { transform: scaleY(0.6); opacity: 0.4; } 40% { transform: scaleY(1.0); opacity: 1.0; } }
      `}</style>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: '9px', color: '#2d7fa0', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '7px' }}>{children}</div>;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> { style?: React.CSSProperties; }

function StyledInput({ style, ...props }: InputProps) {
  const [focused, setFocused] = React.useState(false);
  return (
    <input {...props} onFocus={e => { setFocused(true); props.onFocus?.(e); }} onBlur={e => { setFocused(false); props.onBlur?.(e); }}
      style={{ width: '100%', boxSizing: 'border-box', backgroundColor: focused ? '#ffffff' : '#e8d4a8', border: `1px solid ${focused ? '#1a6688' : '#c4a870'}`, borderRadius: '3px', padding: '12px 14px', color: '#0d3d5c', fontSize: '14px', outline: 'none', fontFamily: 'inherit', marginBottom: '16px', transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease', boxShadow: focused ? '0 0 0 3px rgba(26,102,136,0.10)' : 'none', ...style }} />
  );
}

function LoadingDots() {
  return (
    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
      {[0, 1, 2].map(i => (<span key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#5A7080', display: 'inline-block', animation: `dotBounce 1.2s ease-in-out ${i * 0.15}s infinite` }} />))}
    </span>
  );
}